import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const EMPTY = { avoid: [], veg: "all" };
const SKEY = "z6_filters";
const FiltersContext = createContext(null);
export const ALLERGENS = ["Tree nuts", "Peanut", "Sesame", "Soy", "Fish", "Crustacean", "Gluten", "Dairy", "Egg", "Mustard", "Celery", "Sulphites", "Mushroom", "Seeds", "Caffeine", "Coconut"];
// Religious/cultural practices share the avoid list; each blocks the dish practice_tags it names.
export const PRACTICES = { "No beef": ["beef"], "No pork": ["pork"], "No onion & garlic": ["onion_garlic"], Jain: ["onion_garlic", "root_veg", "meat_fish", "egg"] };
export const PRACTICE_NOTE = "Made without these ingredients. Our kitchen is shared, so guests with strict religious requirements should ask their server.";
const blocked = (d, avoid) => d.allergens.some((a) => avoid.includes(a)) || avoid.some((p) => (PRACTICES[p] || []).some((t) => (d.practice_tags || []).includes(t)));
// Allergen chips a diet already rules out: shown checked and locked.
export const DIET_LOCKS = { veg: ["Fish", "Crustacean"], vegan: ["Fish", "Crustacean", "Dairy", "Egg"] };
export const VEG_NOTE = "Veg doesn't mean free from dairy, nuts or other allergens. Use the allergen filters.";
// Filter diet -> Bio-Q diet answer.
export const BIOQ_DIET = { veg: "vegetarian", vegan: "vegan", nonveg: "everything" };

const TTL = 4 * 3600 * 1000;
const load = () => {
  try {
    const v = JSON.parse(localStorage.getItem(SKEY) || "null");
    return v && Date.now() - v.at < TTL ? { ...EMPTY, ...v.f } : EMPTY;
  } catch { return EMPTY; }
};

export function FiltersProvider({ children }) {
  const [filters, setFilters] = useState(load);
  useEffect(() => { localStorage.setItem(SKEY, JSON.stringify({ f: filters, at: Date.now() })); }, [filters]);
  useEffect(() => {
    const wipe = () => { localStorage.removeItem(SKEY); setFilters(EMPTY); };
    window.addEventListener("z6-reset", wipe);
    return () => window.removeEventListener("z6-reset", wipe);
  }, []);
  const toggle = useCallback((group, value) => {
    setFilters((f) => ({ ...f, [group]: f[group].includes(value) ? f[group].filter((v) => v !== value) : [...f[group], value] }));
  }, []);
  const setVeg = useCallback((veg) => setFilters((f) => ({ ...f, veg })), []);
  const clear = useCallback(() => setFilters(EMPTY), []);
  const locked = DIET_LOCKS[filters.veg] || [];
  const activeCount = filters.avoid.filter((a) => !locked.includes(a)).length + (filters.veg !== "all" ? 1 : 0);
  const value = useMemo(() => ({ filters, toggle, setVeg, clear, activeCount, locked, options: { allergens: ALLERGENS, practices: Object.keys(PRACTICES) } }),
    [filters, toggle, setVeg, clear, activeCount, locked]);
  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export const useFilters = () => useContext(FiltersContext);

const isVeg = (d) => d.diet !== "non-veg";
const isVegan = (d) => d.diet === "vegan";

// Allergens never relax. Veg drops meat/fish/shellfish; vegan drops every animal product. Non-veg falls back to veg for a course with no non-veg dish.
export const filterCourse = (dishes, course, { avoid, veg }) => {
  const base = dishes.filter((d) => d.course === course && !blocked(d, avoid)).sort((a, b) => b.price_inr - a.price_inr);
  if (veg === "veg") return { list: base.filter(isVeg), fallback: false };
  if (veg === "vegan") return { list: base.filter(isVegan), fallback: false };
  if (veg === "nonveg") {
    const nv = base.filter((d) => !isVeg(d));
    return nv.length ? { list: nv, fallback: false } : { list: base, fallback: base.length > 0 };
  }
  return { list: base, fallback: false };
};

export const drinkAllowed = (d, { avoid, veg }) => !blocked(d, avoid) && (veg !== "vegan" || isVegan(d));
